import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@/lib/utils';

/**
 * Shadcn-style wrapper around @radix-ui/react-tooltip. Styling matches the
 * popover tokens already used by Dialog so inviters feel the same visual
 * language across hover surfaces.
 *
 * Usage:
 *   <TooltipProvider delayDuration={200}>
 *     <Tooltip>
 *       <TooltipTrigger asChild><InfoIcon /></TooltipTrigger>
 *       <TooltipContent>Why this number…</TooltipContent>
 *     </Tooltip>
 *   </TooltipProvider>
 *
 * Mount TooltipProvider once at the layout root — not per usage.
 */

const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 overflow-hidden rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md max-w-xs',
        'data-[state=delayed-open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0',
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
